import {
  SpotlightAction as MantineSpotlightAction,
  SpotlightProvider as MantineSpotlightProvider
} from "@mantine/spotlight";
import {IconChartHistogram, IconSearch} from "@tabler/icons-react";
import {PropsWithChildren, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {getStringSorter, useGetAllBaseEntitiesQuery} from "@ots-frontend/ots-data";
import {useNavigate} from "react-router-dom";
import {Loader, rem, Text} from "@mantine/core";
import {Group, SpotlightAction, useApp, useEntitySpotlightAction, useOtsSpotlightAction} from "@ots-frontend/ots-ui";

export const SpotlightProvider = (props: PropsWithChildren<{}>) => {
  const {children} = props;

  const [query, setQuery] = useState("");

  const navigate = useNavigate();
  const {t} = useTranslation(["translation", "common"]);
  const {regionId} = useApp()

  const {entities, isLoading: isEntitiesLoading,} = useGetAllBaseEntitiesQuery({regionId: regionId})

  const {actions: otsActions} = useOtsSpotlightAction({
    query: query
  })

  const pageActions: MantineSpotlightAction[] = useMemo(() => {
    const actions: MantineSpotlightAction[] = []

    if (!query || query.toLowerCase().includes("view") || query.toLowerCase().includes("insights") || query.toLowerCase().includes("alloc") || query.toLowerCase().includes("load") || query.toLowerCase().includes("inventory")) {
      actions.push({
        title: t("common.insights.allocatableloadinventory", "Allocatable Load Inventory") + " " + t("common.insights", "Insights"),
        description: t("common.action.view", "View") + " " + t("common.insights.allocatableloadinventory", "Allocatable Load Inventory"),
        onTrigger: () => {
          navigate("/insights/allocatable-load-inventory");
        },
        keywords: ["view", "insights", "allocatable", "inventory"],
        icon: <IconChartHistogram/>
      })
    }

    if (!query || query.toLowerCase().includes("view") || query.toLowerCase().includes("insights") || query.toLowerCase().includes("volume") || query.toLowerCase().includes("inventory")) {
      actions.push({
        title: t("common.insights.volumeinventory", "Volume Inventory") + " " + t("common.insights", "Insights"),
        description: t("common.action.view", "View") + " " + t("common.insights.volumeinventory", "Volume Inventory"),
        onTrigger: () => {
          navigate("/insights/volume-inventory");
        },
        keywords: ["view", "insights", "volume", "inventory"],
        icon: <IconChartHistogram/>
      })
    }

    return actions

  }, [t, query, navigate]);

  const {actions: siteActions} = useEntitySpotlightAction({
    query: query,
    queryPrefix: 'site',
    entities: entities.sites,
    permissions: ["view", "site_view"],
    onTrigger: (e) => navigate(`/sites/profile?id=${e.id}`)
  })

  const {actions: wellActions} = useEntitySpotlightAction({
    query: query,
    queryPrefix: 'well',
    entities: entities.wells,
    permissions: ["view", "well_view"],
    onTrigger: (e) => navigate(`/wells/profile?id=${e.id}`)
  })

  const {actions: containerActions} = useEntitySpotlightAction({
    query: query,
    queryPrefix: 'container',
    entities: entities.containers,
    permissions: ["view", "container_view"],
    onTrigger: (e) => navigate(`/containers/profile?id=${e.id}`)
  })

  const entityActions: MantineSpotlightAction[] = useMemo(() => {
    return [
      ...siteActions,
      ...wellActions,
      ...containerActions,
    ]
  }, [
    siteActions,
    wellActions,
    containerActions,
  ]);

  const spotlightActions: MantineSpotlightAction[] = useMemo(() => {
    const combinedActions = [...otsActions, ...pageActions, ...entityActions];
    combinedActions.sort(getStringSorter("title"))
    return combinedActions
  }, [otsActions, pageActions, entityActions]);

  return (
    <MantineSpotlightProvider
      actions={spotlightActions}
      actionsWrapperComponent={SpotlightActionsWrapper}
      actionComponent={SpotlightAction}
      query={query}
      onQueryChange={setQuery}
      searchIcon={isEntitiesLoading ? <Loader size="1.2rem"/> : <IconSearch size="1.2rem"/>}
      searchPlaceholder={t("common.action.search", "Search") ?? undefined}
      nothingFoundMessage={t("common.state.empty", "Empty") ?? undefined}
      highlightQuery={true}
      filter={(q, actions) => actions}
      shortcut={["mod + K"]}
    >
      {children}
    </MantineSpotlightProvider>
  );
};


export const SpotlightActionsWrapper = (props: PropsWithChildren<NonNullable<unknown>>) => {
  const {children} = props;
  return (
    <div>
      <Group
        position="apart"
        px={15}
        py="xs"
        sx={(theme) => ({
          borderBottom: `${rem(1)} solid ${
            theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[2]
          }`,
        })}
      >
        <Text size="xs" color="dimmed">
          To search for specific entities: use the prefixes such as <Text span fw="bold" inherit>well://</Text> , <Text span fw="bold" inherit>container://</Text> , or <Text span fw="bold" inherit>site://</Text>
        </Text>
      </Group>
      {children}
    </div>
  );
}
